import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery

// Components 
import Box from '@mui/material/Box';
import { TextField } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { verifySecurityPin } from '../../../routes/general/authentication';

const Auth: React.FC = () => {
    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const isMobile = useMediaQuery('(max-width: 768px)'); // Use useMediaQuery

    const colors = useAppSelector(state => state.theme.colors); 

    const [error, setError] = useState(false); 
    const [pin0, setPin0] = useState(''); 
    const [pin1, setPin1] = useState(''); 
    const [pin2, setPin2] = useState(''); 
    const [pin3, setPin3] = useState(''); 

    const pin0Ref = useRef<HTMLInputElement>(null); 
    const pin1Ref = useRef<HTMLInputElement>(null); 
    const pin2Ref = useRef<HTMLInputElement>(null);  
    const pin3Ref = useRef<HTMLInputElement>(null); 

    const handleSecurityPin = (number: string, order: number) => { 
        const reg = new RegExp('^[0-9]$'); // Only allow a single digit
        if (number && !reg.test(number)) { 
            return; 
        }

        const value = number.slice(-1); // Ensure only one character

        switch(order) {
            case 0: setPin0(value); if (value) pin1Ref.current?.focus(); break;
            case 1: setPin1(value); if (value) pin2Ref.current?.focus(); else pin0Ref.current?.focus(); break;
            case 2: setPin2(value); if (value) pin3Ref.current?.focus(); else pin1Ref.current?.focus(); break;
            case 3: setPin3(value); if (!value) pin2Ref.current?.focus(); break;
        }
        setError(false); 
    }; 

    const handleVerifySecurityPin = async () => { 
        const res = await verifySecurityPin(`${pin0}${pin1}${pin2}${pin3}`) as any; 
        if (res?.data?.status === 200) { 
            const previousLocation = localStorage.getItem('lockScreenRedirect'); 
            localStorage.setItem('isScreenLocked', 'off');
            previousLocation ? navigate(previousLocation) : navigate('/dashboard'); 
            localStorage.removeItem('lockScreenRedirect'); 
        } else if (res?.data?.status === 403) { 
            setError(true); 
        } else { 
            navigate('/login'); 
        }; 
    }; 

    useEffect(() => { 
        if (pin0 && pin1 && pin2 && pin3) { 
            handleVerifySecurityPin(); 
        }; 
    },[pin0, pin1, pin2, pin3]); 

    const textFieldStyle = { 
        width: isMobile ? 45 : 60, // Use isMobile for responsive width
        height: isMobile ? 45 : 60, // Use isMobile for responsive height
        margin: 0.5,
        color: colors?.text,
        '& .MuiOutlinedInput-root': {
            height: '100%',
            '& fieldset': {
              borderColor: error ? colors?.red : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: error ? colors?.red : colors?.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: error ? colors?.red : colors?.main,
            },
        },
    }; 

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '90%',
            maxWidth: 400,
            padding: isMobile ? 3 : 4, // Use isMobile for responsive padding
            borderRadius: 3, 
            backgroundColor: colors?.container,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
            <Box
                component="img"
                sx={{ height: 35, width: 'auto', mb: 3 }}
                alt="Solutions Medca Inc."
                src={require('../../../assets/logos/medca_logo_blue_dark_blue.png')}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', color: colors?.text, mb: 1 }}>
                <LockOutlinedIcon sx={{ fontSize: 22, mr: 1, color: colors?.text }} />
                <Box sx={{ fontSize: 22, fontWeight: 'bold' }}>
                    {t('securityPin')}
                </Box>
            </Box>
            
            <Box sx={{ color: 'rgb(120, 120, 120)', fontSize: 14, mb: 2, textAlign: 'center' }}>
                {t('unlockSession')}
            </Box>

            {error && 
            <Box sx={{ fontSize: 14, color: colors?.red, mb: 2 }}>
                {t('invalidPin')}
            </Box>}

            <Box
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center', 
                    my: 3, 
                }}
            >
                <TextField 
                    sx={textFieldStyle} 
                    inputProps={{ style: { textAlign: 'center', fontSize: 20, color: colors?.text }, maxLength: 1 }}
                    onChange={(e) => handleSecurityPin(e.target.value, 0)}
                    id="pin-0" 
                    value={pin0}
                    variant="outlined" 
                    autoFocus
                    type='password'
                    inputRef={pin0Ref}
                />
                <TextField 
                    sx={textFieldStyle} 
                    inputProps={{ style: { textAlign: 'center', fontSize: 20, color: colors?.text }, maxLength: 1 }}
                    onChange={(e) => handleSecurityPin(e.target.value, 1)}
                    id="pin-1" 
                    value={pin1}
                    variant="outlined" 
                    type='password'
                    inputRef={pin1Ref}
                />
                <TextField 
                    sx={textFieldStyle} 
                    inputProps={{ style: { textAlign: 'center', fontSize: 20, color: colors?.text }, maxLength: 1 }}
                    onChange={(e) => handleSecurityPin(e.target.value, 2)}
                    id="pin-2" 
                    value={pin2}
                    variant="outlined" 
                    type='password'
                    inputRef={pin2Ref}
                />
                <TextField 
                    sx={textFieldStyle} 
                    inputProps={{ style: { textAlign: 'center', fontSize: 20, color: colors?.text }, maxLength: 1 }}
                    onChange={(e) => handleSecurityPin(e.target.value, 3)}
                    id="pin-3" 
                    value={pin3}
                    variant="outlined" 
                    type='password'
                    inputRef={pin3Ref}
                />
            </Box>

            <Box sx={{ display: 'flex', fontSize: 14, color: 'rgb(120, 120, 120)', mt: 3, textAlign: 'center' }}>
                <Box sx={{ mr: 0.5 }}>{t('pinIssueInfo')}</Box>
                <Box
                    onClick={() => navigate('/login')}
                    sx={{ 
                        color: colors?.main, 
                        cursor: 'pointer',
                        fontWeight: '500',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {t('login')}
                </Box>
            </Box>
        </Box>
    );
};

export default Auth;